import * as _ from 'lodash';
import type { Column } from 'react-table';
import { useDialogContext } from '../../../../contexts/DialogContext';
import { BUTTON_TYPE } from '../../../generic';
import PopperMenu from '../../../generic/PopperMenu';
import SimpleReactTable, { MINIMUM_PAGE_SIZE } from '../../../generic/SimpleReactTable';
import TableHeader from '../../../generic/TableHeader';
import type { OrganizationAccessRequest } from '../../../../types';

type OrganizationAccessRequestsTableProps = {
  accessRequests: Record<string, OrganizationAccessRequest>;
  searchQuery: string;
  onAcceptRequest(accessRequestId: number): void;
  onIgnoreRequest(accessRequestId: number): void;
};

const OrganizationAccessRequestsTable = (props: OrganizationAccessRequestsTableProps) => {
  const { onConfirm } = useDialogContext();

  let filteredRequests = Object.values(props.accessRequests);

  if (props.searchQuery) {
    filteredRequests = filteredRequests.filter(
      (request) => request.email.toLowerCase().includes(props.searchQuery.toLowerCase()),
    );
  }

  const acceptRequestFn = (request: OrganizationAccessRequest) => () =>
    onConfirm({
      title: 'Confirm Accept request',
      body: (
        <>
          <p>Are you sure you want to accept this access request?</p>
          <p>This will send an invite email to &ldquo;{request.email}&rdquo; to join your organization in FOSSA.</p>
        </>
      ),
      onConfirm: () => props.onAcceptRequest(request.id),
      confirmBtnType: BUTTON_TYPE.PRIMARY,
    });

  const ignoreRequestFn = (request: OrganizationAccessRequest) => () =>
    onConfirm({
      title: `${request.fulfilled ? 'Remove' : 'Ignore'} request?`,
      body: (
        <p>
          Are you sure you want to {request.fulfilled ? 'remove' : 'ignore'} this access request?
        </p>
      ),
      onConfirm: () => props.onIgnoreRequest(request.id),
    });

  const columns: Column[] = [
    {
      id: 'email',
      Header: (
        <TableHeader title="Requester’s Email" />
      ),
      accessor: 'email',
      resizable: false,
      // eslint-disable-next-line react/no-unstable-nested-components -- This is fine for now.
      Cell: ({ original }: OrganizationAccessRequest) => (
        <>
          <span>{original.email}{original.fulfilled && ' (Invite sent)'}</span>
          <PopperMenu>
            {!original.fulfilled && (
              <PopperMenu.Item onClick={acceptRequestFn(original)}>
                Invite user
              </PopperMenu.Item>
            )}
            <PopperMenu.Item onClick={ignoreRequestFn(original)}>
              {original.fulfilled ? 'Remove' : 'Ignore'} request
            </PopperMenu.Item>
          </PopperMenu>
        </>
      ),
      style: {
        overflow: 'visible',
        paddingRight: '15px',
      },
    },
  ];

  return (
    <SimpleReactTable
      data={filteredRequests}
      columns={columns}
      defaultSortMode={[{ id: 'email', desc: false }]}
      onSortedChanged={_.noop}
      showPagination={filteredRequests.length > MINIMUM_PAGE_SIZE}
      cacheId="organizationAccessRequests"
    />
  );
};

export default OrganizationAccessRequestsTable;
